// Organisation overview — the first screen an admin lands on.
//
// It used to render `ADMIN_ORG` + `ACTIVITY` (fixtures for "Bright Steps
// Centre") to EVERY admin, so a real organisation was shown someone else's
// numbers — "5 of 12 seats", "7 linked children" — and an activity feed naming
// people who do not exist, as if they were their own. That is the single most
// visible lie the product could tell, so this screen now follows the same
// demo/live split as Members and Seats & licence:
//
//   Overview        → dispatcher (shouldUseSupabase?)
//   OverviewView    → one presentational component, plain props
//   OverviewDemo    → fixtures (what the existing test suite exercises)
//   OverviewLive    → org_overview + org_activity for THIS org, with real
//                     loading / empty / error states.
//
// The org id comes from `useOrg()` — never re-derived from memberships (that
// old `?? memberships[0] ?? ""` fallback was a confirmed bug).
//
// PRIVACY: the child figure here is a COUNT and nothing else. No child ref, no
// label, no progress ever reaches an org admin.

import { OrgAnalyticsPanel } from "./Analytics";
import { useOrgProfile } from "./orgProfile";
import { Chip } from "./ui";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { portalMode, adminPath } from "../../lib/host";
import { useComfort } from "../ComfortContext";
import { getDictionary } from "../../i18n/dictionary";
import { getPortalData, shouldUseSupabase } from "../../data";
import { PortalWriteError } from "../../data/supabaseAdapter";
import type { OrgActivityEntry, OrgActivityKind, OrgOverview } from "../../data/types";
import { useOrg } from "./OrgContext";
import { ADMIN_ORG, ACTIVITY } from "./data";

// ---- Activity feed shape -----------------------------------------------------

export interface ActivityFeedItem {
  id: string;
  kind: OrgActivityKind;
  text: string;
  /** Already-formatted, human date — see {@link formatActivityTime}. */
  timeLabel: string;
}

// ---- Date formatting ---------------------------------------------------------

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * Turns an ISO timestamp from `org_activity.at` into a calm, human label:
 * "Today · 09:30", "Yesterday", "14 Jun", "14 Jun 2025".
 *
 * Pure and injectable (`now`) so it is unit-testable without freezing the
 * clock. Anything unparseable degrades to an em dash rather than "Invalid Date".
 */
export function formatActivityTime(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";

  const dayDiff = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);

  // <= 0 also covers a timestamp a few seconds in the future (clock skew
  // between the browser and Postgres), which should still read as "Today".
  if (dayDiff <= 0) {
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `Today · ${hh}:${mm}`;
  }
  if (dayDiff === 1) return "Yesterday";

  const label = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return d.getFullYear() === now.getFullYear()
    ? label
    : `${label} ${d.getFullYear()}`;
}

/** Calm copy for a failed read, keyed on the Postgres SQLSTATE — never on message text. */
export function overviewErrorCopy(err: unknown): string {
  const code = err instanceof PortalWriteError ? err.code : undefined;
  if (code === "42501") {
    return "You don't have permission to view this organisation. Try signing in again.";
  }
  if (code === "P0002") {
    return "We couldn't find this organisation. It may have been removed.";
  }
  if (code === "P0001") {
    return "We couldn't put your overview together. Please try again.";
  }
  return "We couldn't load your overview just now. Please try again in a moment.";
}

// ---- Activity icons ----------------------------------------------------------

function ActivityIcon({ kind }: { kind: OrgActivityKind }) {
  if (kind === "member") {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="8" r="4" />
        <path d="M6 20a6 6 0 0112 0" />
      </svg>
    );
  }
  if (kind === "seat") {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 11V8a2 2 0 012-2h10a2 2 0 012 2v3" />
        <path d="M4 11h16a1 1 0 011 1v4H3v-4a1 1 0 011-1z" />
        <path d="M6 16v3M18 16v3" />
      </svg>
    );
  }
  if (kind === "invite") {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="5" width="18" height="14" rx="3" />
        <path d="M3 7l9 6 9-6" />
      </svg>
    );
  }
  // org
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 21h18M4 21V7l8-4 8 4v14" />
      <path d="M9 21v-6h6v6" />
    </svg>
  );
}

const ACTIVITY_TINT: Record<OrgActivityKind, string> = {
  member: "var(--sky-tint)",
  seat: "var(--sage-tint)",
  invite: "var(--brand-tint)",
  org: "var(--lilac-tint)",
};

const ACTIVITY_COLOR: Record<OrgActivityKind, string> = {
  member: "var(--sky-deep)",
  seat: "var(--sage-deep)",
  invite: "var(--brand-hover)",
  org: "var(--lilac-deep)",
};

// ---- Stat tile ---------------------------------------------------------------

function StatTile({
  count,
  label,
  sub,
  iconBg,
  icon,
}: {
  count: number;
  label: string;
  sub: string;
  iconBg: string;
  icon: React.ReactNode;
}) {
  return (
    <div style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-lg)", boxShadow: "var(--sh-1)", padding: 20 }}>
      <span style={{ width: 44, height: 44, borderRadius: "var(--r-md)", background: iconBg, display: "grid", placeItems: "center" }}>
        {icon}
      </span>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 14 }}>
        <b style={{ fontFamily: "var(--font-display)", fontSize: 28, color: "var(--ink-deep)" }}>{count}</b>
        <span style={{ fontSize: 14.5, color: "var(--ink-soft)" }}>{label}</span>
      </div>
      <span style={{ fontSize: 13, color: "var(--ink-faint)" }}>{sub}</span>
    </div>
  );
}

// ---- Page header (shared by the loaded, loading and error states) ------------

function PageHeader({ title, subtitle, inviteLabel }: { title: string; subtitle: string; inviteLabel: string }) {
  const navigate = useNavigate();
  // adminPath, never a hardcoded "/admin/…". On admin.zooylo.app the admin
  // routes are served at the ROOT ("/members"), so "/admin/members" 404s there
  // — and that is the only host real admins use.
  const mode = portalMode();
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
      <div>
        <h1 style={{ fontSize: 30, color: "var(--ink-deep)", letterSpacing: "-.02em", margin: 0 }}>{title}</h1>
        <p style={{ color: "var(--ink-soft)", marginTop: 8, fontSize: 16.5, margin: "8px 0 0" }}>{subtitle}</p>
      </div>
      <button
        onClick={() => navigate(adminPath("members", mode))}
        style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 9, minHeight: 48, padding: "0 22px", border: 0, borderRadius: "var(--r-pill)", background: "var(--brand)", color: "#fff", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 16, cursor: "pointer", boxShadow: "var(--sh-1)" }}
      >
        <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 5v14M5 12h14" />
        </svg>
        {inviteLabel}
      </button>
    </div>
  );
}

// ---- Presentational view -----------------------------------------------------

export interface OverviewViewProps {
  seatsTotal: number;
  seatsUsed: number;
  memberCount: number;
  adminCount: number;
  linkedChildCount: number;
  pendingInviteCount: number;
  activity: ActivityFeedItem[];
}

export function OverviewView({
  seatsTotal,
  seatsUsed,
  memberCount,
  adminCount,
  linkedChildCount,
  pendingInviteCount,
  activity,
}: OverviewViewProps) {
  const { locale } = useComfort();
  const dict = getDictionary(locale);
  const dictAll = dict;
  const t = dict.admin.overview;
  const orgStatus = useOrgProfile().status;

  const seatsFree = Math.max(0, seatsTotal - seatsUsed);
  // A brand-new organisation can legitimately have no licence rows yet — never
  // divide by zero and never render NaN%.
  const seatPct = seatsTotal > 0 ? Math.round((seatsUsed / seatsTotal) * 100) : 0;
  const memberSub = `${adminCount} admin · ${memberCount - adminCount} ${t.membersWord}`;

  return (
    <>
      {/* Page header */}
      <PageHeader title={t.title} subtitle={t.subtitle} inviteLabel={dict.admin.members.inviteButton} />

      {/* Licence health */}
      <div style={{ marginTop: 24, border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-lg)", boxShadow: "var(--sh-1)", padding: 24 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--ink-faint)" }}>{t.licenceHealth}</span>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 8 }}>
              <b style={{ fontFamily: "var(--font-display)", fontSize: 38, color: "var(--ink-deep)", letterSpacing: "-.02em", lineHeight: 1 }}>{seatsUsed}</b>
              <span style={{ fontSize: 17, color: "var(--ink-soft)" }}>of {seatsTotal} seats in use</span>
            </div>
          </div>
          {orgStatus === "active" ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 15px", borderRadius: "var(--r-pill)", background: "var(--sage-tint)", color: "var(--sage-deep)", fontSize: 13.5, fontWeight: 600 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--good)", display: "inline-block" }} />
              {t.planActive}
            </span>
          ) : (
            // Never "Plan active" on an org Zooylo has paused or closed.
            <Chip tone="attention">{orgStatus === "closed" ? dictAll.console.stClosed : dictAll.console.stSuspended}</Chip>
          )}
        </div>
        <div style={{ marginTop: 16, height: 14, borderRadius: "var(--r-pill)", background: "var(--surface-sunk)", overflow: "hidden" }}>
          <span style={{ display: "block", height: "100%", width: `${seatPct}%`, borderRadius: "var(--r-pill)", background: "var(--brand)" }} />
        </div>
        <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--ink-faint)" }}>
          <span>{seatsFree} {t.seatsFreeLabel}</span>
          <span>{t.needMoreSeats}</span>
        </div>
      </div>

      {/* Stat tiles */}
      <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: 16 }}>
        <StatTile
          count={memberCount}
          label={t.membersWord}
          sub={memberSub}
          iconBg="var(--sky-tint)"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="var(--sky-deep)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="9" cy="8" r="3" />
              <path d="M3 19a6 6 0 0112 0" />
              <path d="M16 6a3 3 0 010 6" />
            </svg>
          }
        />
        <StatTile
          count={linkedChildCount}
          label={t.linkedChildrenLabel}
          sub={t.pseudonymousNote}
          iconBg="var(--sage-tint)"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="var(--sage-deep)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z" />
            </svg>
          }
        />
        <StatTile
          count={pendingInviteCount}
          label={t.pendingInviteLabel}
          sub={t.pendingWhoLabel}
          iconBg="var(--peach-tint)"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="var(--peach-deep)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 12a9 9 0 1018 0 9 9 0 00-18 0z" />
              <path d="M12 7v5l3 2" />
            </svg>
          }
        />
      </div>

      {/* Recent activity */}
      <div style={{ marginTop: 18, border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-lg)", boxShadow: "var(--sh-1)", padding: 24 }}>
        <h3 style={{ fontSize: 18, color: "var(--ink-deep)", margin: 0 }}>{t.recentActivity}</h3>
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column" }}>
          {activity.map((item) => (
            <div key={item.id} style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "14px 0", borderBottom: "1px solid var(--line)" }}>
              <span style={{ width: 40, height: 40, borderRadius: "var(--r-md)", background: ACTIVITY_TINT[item.kind], color: ACTIVITY_COLOR[item.kind], display: "grid", placeItems: "center", flexShrink: 0 }}>
                <ActivityIcon kind={item.kind} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 15, color: "var(--ink)", lineHeight: 1.5 }}>{item.text}</span>
              </div>
              <span style={{ fontSize: 13, color: "var(--ink-faint)", whiteSpace: "nowrap", paddingTop: 2 }}>{item.timeLabel}</span>
            </div>
          ))}

          {/* Day-one state: a brand-new organisation has no history yet. This is
              the COMMON case, so it must read as intentional, not broken. */}
          {activity.length === 0 && (
            <p style={{ margin: "6px 0 0", fontSize: 15, color: "var(--ink-faint)", lineHeight: 1.6 }}>
              Nothing yet — activity will appear here as you invite people and assign seats.
            </p>
          )}
        </div>
      </div>
    </>
  );
}

// ---- Live-only loading + error states ----------------------------------------

function OverviewLoading({ title, subtitle, inviteLabel }: { title: string; subtitle: string; inviteLabel: string }) {
  return (
    <>
      <PageHeader title={title} subtitle={subtitle} inviteLabel={inviteLabel} />
      <div
        role="status"
        aria-live="polite"
        style={{ marginTop: 24, border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-lg)", boxShadow: "var(--sh-1)", padding: 24 }}
      >
        <span style={{ fontSize: 15.5, color: "var(--ink-soft)" }}>
          Loading your organisation's numbers…
        </span>
        <div style={{ marginTop: 16, display: "grid", gap: 12 }} aria-hidden="true">
          <span style={{ display: "block", height: 14, width: "45%", borderRadius: "var(--r-pill)", background: "var(--surface-sunk)" }} />
          <span style={{ display: "block", height: 14, width: "70%", borderRadius: "var(--r-pill)", background: "var(--surface-sunk)" }} />
          <span style={{ display: "block", height: 14, width: "60%", borderRadius: "var(--r-pill)", background: "var(--surface-sunk)" }} />
        </div>
      </div>
    </>
  );
}

function OverviewError({
  title,
  subtitle,
  inviteLabel,
  message,
  onRetry,
}: {
  title: string;
  subtitle: string;
  inviteLabel: string;
  message: string;
  onRetry: () => void;
}) {
  return (
    <>
      <PageHeader title={title} subtitle={subtitle} inviteLabel={inviteLabel} />
      <div
        role="status"
        aria-live="polite"
        style={{ marginTop: 24, border: "1px solid var(--peach-deep)", background: "var(--peach-tint)", borderRadius: "var(--r-lg)", padding: 24 }}
      >
        <h2 style={{ fontSize: 18, color: "var(--ink-deep)", margin: 0 }}>
          We couldn't load your overview
        </h2>
        <p style={{ margin: "8px 0 0", fontSize: 15.5, color: "var(--ink-soft)", lineHeight: 1.6 }}>
          {message}
        </p>
        <button
          onClick={onRetry}
          style={{ marginTop: 16, minHeight: 44, padding: "0 20px", border: "1.5px solid var(--peach-deep)", background: "transparent", color: "var(--peach-deep)", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 15, borderRadius: "var(--r-pill)", cursor: "pointer" }}
        >
          Try again
        </button>
      </div>
    </>
  );
}

// ---- Dispatcher + demo/live wrappers -----------------------------------------

/** Real org counts + audit feed when Supabase is configured, else the demo data. */
export function Overview() {
  return (
    <>
      {shouldUseSupabase() ? <OverviewLive /> : <OverviewDemo />}
      <OrgAnalyticsPanel />
    </>
  );
}

function OverviewDemo() {
  return (
    <OverviewView
      seatsTotal={ADMIN_ORG.seatsTotal}
      seatsUsed={ADMIN_ORG.seatsUsed}
      memberCount={ADMIN_ORG.memberCount}
      adminCount={ADMIN_ORG.adminCount}
      linkedChildCount={ADMIN_ORG.linkedChildCount}
      pendingInviteCount={ADMIN_ORG.pendingInviteCount}
      activity={ACTIVITY}
    />
  );
}

function OverviewLive() {
  const { locale } = useComfort();
  const dict = getDictionary(locale);
  const t = dict.admin.overview;
  const inviteLabel = dict.admin.members.inviteButton;

  // Always a real org id: the gate only renders the admin shell once the org
  // has resolved to status "admin".
  const { orgId } = useOrg();

  const [overview, setOverview] = useState<OrgOverview | null>(null);
  const [activity, setActivity] = useState<ActivityFeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    (async () => {
      const data = getPortalData();

      // The two reads are deliberately NOT coupled with Promise.all. The counts
      // are the point of this screen; the activity feed is context. Joining them
      // meant a failure of the secondary read hid the primary content behind an
      // error card — the admin loses their seat and member numbers because an
      // audit feed timed out. The feed degrades to empty instead.
      const feedPromise = data
        .orgActivity(orgId, 8)
        .catch((): OrgActivityEntry[] => []);

      try {
        const ov = await data.orgOverview(orgId);
        if (cancelled) return;
        setOverview(ov);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        // A read failure must never fall through to an empty list — that reads
        // as "you have nothing", which is a lie.
        setError(overviewErrorCopy(err));
        setLoading(false);
        return;
      }

      const feed = await feedPromise;
      if (cancelled) return;
      const now = new Date();
      setActivity(
        feed.map((e) => ({
          id: e.id,
          kind: e.kind,
          text: e.text,
          timeLabel: formatActivityTime(e.at, now),
        })),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId, attempt]);

  if (loading) {
    return <OverviewLoading title={t.title} subtitle={t.subtitle} inviteLabel={inviteLabel} />;
  }

  if (error || !overview) {
    return (
      <OverviewError
        title={t.title}
        subtitle={t.subtitle}
        inviteLabel={inviteLabel}
        message={error || "We couldn't find this organisation. It may have been removed."}
        onRetry={retry}
      />
    );
  }

  return (
    <OverviewView
      seatsTotal={overview.seatsTotal}
      seatsUsed={overview.seatsUsed}
      memberCount={overview.memberCount}
      adminCount={overview.adminCount}
      linkedChildCount={overview.linkedChildCount}
      pendingInviteCount={overview.pendingInviteCount}
      activity={activity}
    />
  );
}
